import { Autocomplete, CircularProgress, TextField, Card, Chip, Grid, Icon, IconButton, InputLabel, Tooltip, Checkbox, FormControlLabel, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDateTime } from "utils/DateUtils";
import { cleanFormatCNPJ } from "utils/Functions";
import { post, get, postNoResponse } from "utils/WebService";
import DevolucaoDiretaDialog from "./DevolucaoDiretaDialog";
import BaseDeDadosCreateEdit from "./BaseDeDadosCreateEdit";
import MDSnackbar from "components/MDSnackbar";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import routes from "routes";
import { TIPO_PERFIL, podeDevolverOuMigrarDireto, temTipoPerfil } from "utils/Perfis";
import { ESCURO } from "assets/coresTema";

export default function BaseDeDadosConsult({ onPageSelect }) {

    const { processoStatus, userFunctionalities, userPerfils } = useUser();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Veem o filtro de fase
    const isLiberado = temTipoPerfil(userPerfils,
        TIPO_PERFIL.GESTOR, TIPO_PERFIL.COORDENADOR, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);
    // Devolvem/reativam a ficha direto pela lista; os demais solicitam pela ficha (Fila de Solicitações)
    const podeDevolverDireto = podeDevolverOuMigrarDireto(userPerfils, userFunctionalities);
    const hideFaseFilter = !isLiberado;

    const faseOptions = [
        { id: null, nome: "Todos" },
        { id: false, nome: "Extrajudicial" },
        { id: true, nome: "Jurídica" }
    ];

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        cvId: "",
        nome: "",
        cpfCnpj: "",
        codigoCliente: "",
        status: null,
        idUsuario: null,
        fase: null,
        hideDevolvidos: false,
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(false);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedFichaId, setSelectedFichaId] = useState(null);
    const [isJuridicoForm, setIsJuridicoForm] = useState(false);
    const [openedFromOrigin, setOpenedFromOrigin] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [devedorToDelete, setDevedorToDelete] = useState(null);
    const [devolucaoDireta, setDevolucaoDireta] = useState(null);
    const [reativarDialogOpen, setReativarDialogOpen] = useState(false);
    const [devedorToReativar, setDevedorToReativar] = useState(null);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const [filters, setFilters] = useState({
        cvId: "",
        nome: "",
        cpfCnpj: "",
        codigoCliente: "",
        status: null,
        idUsuario: { id: null, nome: "Todos" },
        fase: faseOptions[0],
        hideDevolvidos: false,
        comSaldo: false,
        cliente: null,
    });

    const [pessoaOptions, setPessoaOptions] = useState([]);
    const [loadingPessoas, setLoadingPessoas] = useState(false);
    const searchTimeout = useRef(null);
    const handleSearchPessoas = (val) => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setLoadingPessoas(true);
            get(`/pessoa/list-all?search=${val || ""}`).then(({ list }) => {
                setPessoaOptions(list || []);
                setLoadingPessoas(false);
            }).catch(() => setLoadingPessoas(false));
        }, 500);
    };

    useEffect(() => {
        handleSearchPessoas("");
    }, []);

    useEffect(() => {
        const handlePending = (e) => {
            const pendingId = e?.detail?.id || sessionStorage.getItem('pendingDevedorId') || localStorage.getItem('pendingDevedorId');
            const origin = e?.detail?.origin || sessionStorage.getItem('pendingDevedorOrigin') || localStorage.getItem('pendingDevedorOrigin');
            if (pendingId) {
                sessionStorage.removeItem('pendingDevedorId');
                localStorage.removeItem('pendingDevedorId');
                if (origin) {
                    sessionStorage.removeItem('pendingDevedorOrigin');
                    localStorage.removeItem('pendingDevedorOrigin');
                    setOpenedFromOrigin(origin);
                }
                setSelectedId(pendingId);
                setIsViewOnly(true);
                setIsCreateEdit(true);
            }
        };

        handlePending();
        window.addEventListener('pendingDevedorChange', handlePending);
        return () => {
            window.removeEventListener('pendingDevedorChange', handlePending);
        };
    }, []);

    const [statusFichaOptions, setStatusFichaOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([{ id: null, nome: "Todos" }]);
    const [userEquipeIsJuridico, setUserEquipeIsJuridico] = useState(false);

    useEffect(() => {
        get("/status-ficha/list-all").then((res) => {
            setStatusFichaOptions(res.list || res.data || (Array.isArray(res) ? res : []));
        }).catch(() => {});

        get(`/users/list-all-operators`).then((res) => {
            const list = res.list || res.data || (Array.isArray(res) ? res : []);
            setUsuariosOptions([{ id: null, nome: "Todos" }, ...list]);
        }).catch(() => {});

        get(`/users/is-juridico`).then((res) => {
            if (res && res.isJuridico !== undefined) {
                setUserEquipeIsJuridico(Boolean(res.isJuridico));
            }
        }).catch(() => {});
    }, []);

    const [recordsTotal, setRecordsTotal] = useState(0);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered, cvId, nome, cpfCnpj, codigoCliente, titulo, processo, status, idUsuario, fase, hideDevolvidos, comSaldo, cliente }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
            cvId: cvId !== undefined ? cvId : tableState.current.cvId,
            nome: nome !== undefined ? nome : tableState.current.nome,
            cpfCnpj: cpfCnpj !== undefined ? cpfCnpj : tableState.current.cpfCnpj,
            codigoCliente: codigoCliente !== undefined ? codigoCliente : tableState.current.codigoCliente,
            titulo: titulo !== undefined ? titulo : tableState.current.titulo,
            processo: processo !== undefined ? processo : tableState.current.processo,
            status: status !== undefined ? status : tableState.current.status,
            idUsuario: idUsuario !== undefined ? idUsuario : tableState.current.idUsuario,
            fase: fase !== undefined ? fase : tableState.current.fase,
            hideDevolvidos: hideDevolvidos !== undefined ? hideDevolvidos : tableState.current.hideDevolvidos,
            comSaldo: comSaldo !== undefined ? comSaldo : tableState.current.comSaldo,
            cliente: cliente !== undefined ? cliente : tableState.current.cliente,
        };

        tableState.current = updatedTableState;

        const selectedStatusIds = Array.isArray(updatedTableState.status)
            ? updatedTableState.status
                .filter(s => s && (typeof s === 'object' ? (s.id !== null && s.id !== undefined && s.id !== "") : s !== null))
                .map(s => String(typeof s === 'object' ? s.id : s))
            : (updatedTableState.status && (typeof updatedTableState.status !== 'object' || (updatedTableState.status.id !== null && updatedTableState.status.id !== undefined && updatedTableState.status.id !== ""))
                ? [String(typeof updatedTableState.status === 'object' ? updatedTableState.status.id : updatedTableState.status)]
                : []);

        const selectedFase = (updatedTableState.fase !== null && updatedTableState.fase !== undefined && typeof updatedTableState.fase === 'object')
            ? updatedTableState.fase.id
            : updatedTableState.fase;

        post("/devedor/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            cvId: updatedTableState.cvId ? parseInt(updatedTableState.cvId) : null,
            ccNome: updatedTableState.nome,
            ccDocumento: updatedTableState.cpfCnpj,
            ccCodigoCliente: updatedTableState.codigoCliente,
            ccTitulo: updatedTableState.titulo,
            ccProcesso: updatedTableState.processo,
            idStatusFicha: selectedStatusIds.length === 1 ? selectedStatusIds[0] : null,
            idsStatusFicha: selectedStatusIds.length > 0 ? selectedStatusIds : null,
            idUsuario: (updatedTableState.idUsuario && typeof updatedTableState.idUsuario === 'object')
                ? (updatedTableState.idUsuario.id ?? null)
                : (updatedTableState.idUsuario ?? null),
            hideDevolvidos: updatedTableState.hideDevolvidos,
            comSaldo: updatedTableState.comSaldo,
            idPessoa: updatedTableState.cliente ? updatedTableState.cliente.id : null,
            isJuridico: selectedFase,

        }).then(({ data, pages, recordsTotal }) => {
            const flattenedData = [];
            if (data) {
                data.forEach(dev => {
                    if (dev.fichas && dev.fichas.length > 0) {
                        let filteredFichas = dev.fichas;

                        // Filtra a lista de fichas local se filtro de Fase foi aplicado
                        if (selectedFase === true || selectedFase === false) {
                            filteredFichas = filteredFichas.filter(f => Boolean(f.isJuridico) === selectedFase);
                        }

                        // Filtra a lista de fichas local se filtro de Status foi aplicado
                        if (selectedStatusIds.length > 0) {
                            filteredFichas = filteredFichas.filter(f => f.idStatusFicha !== undefined && selectedStatusIds.includes(String(f.idStatusFicha)));
                        }

                        // Filtra a lista de fichas local se filtro de Saldo foi aplicado
                        if (updatedTableState.comSaldo) {
                            filteredFichas = filteredFichas.filter(f => f.saldoTotal && Number(f.saldoTotal) > 0);
                        }

                        if (filteredFichas.length > 0) {
                            filteredFichas.forEach(ficha => {
                                flattenedData.push({
                                    ...dev,
                                    nomeCliente: ficha.isHonorario ? `${dev.nomeCliente} (HO)` : dev.nomeCliente,
                                    nomeCarteira: ficha.nomeCarteira || dev.nomeCarteira || "-",
                                    status: ficha.situacao !== undefined ? ficha.situacao : dev.status,
                                    statusFichaNome: ficha.nomeStatusFicha || dev.statusFichaNome,
                                    nomeUsuario: ficha.nomeUsuarioDono || ficha.nomeUsuario || dev.nomeUsuario || dev.responsavel,
                                    responsavel: ficha.nomeUsuarioDono || ficha.nomeUsuario || dev.responsavel || dev.nomeUsuario,
                                    cvIdFicha: ficha.id || ficha.rawId,
                                    saldoTotal: ficha.saldoTotal,
                                    dataDevolucao: ficha.dataDevolucao || dev.dataDevolucao,
                                    nomeUsuarioDevolucao: ficha.nomeUsuarioDevolucao || dev.nomeUsuarioDevolucao
                                });
                            });
                        } else if (selectedFase === null && selectedStatusIds.length === 0 && !updatedTableState.comSaldo) {
                            dev.fichas.forEach(ficha => {
                                flattenedData.push({
                                    ...dev,
                                    nomeCliente: ficha.isHonorario ? `${dev.nomeCliente} (HO)` : dev.nomeCliente,
                                    nomeCarteira: ficha.nomeCarteira || dev.nomeCarteira || "-",
                                    status: ficha.situacao !== undefined ? ficha.situacao : dev.status,
                                    statusFichaNome: ficha.nomeStatusFicha || dev.statusFichaNome,
                                    nomeUsuario: ficha.nomeUsuarioDono || ficha.nomeUsuario || dev.nomeUsuario || dev.responsavel,
                                    responsavel: ficha.nomeUsuarioDono || ficha.nomeUsuario || dev.responsavel || dev.nomeUsuario,
                                    cvIdFicha: ficha.id || ficha.rawId,
                                    saldoTotal: ficha.saldoTotal,
                                    dataDevolucao: ficha.dataDevolucao || dev.dataDevolucao,
                                    nomeUsuarioDevolucao: ficha.nomeUsuarioDevolucao || dev.nomeUsuarioDevolucao
                                });
                            });
                        } else if (!updatedTableState.comSaldo) {
                            // Se nenhuma ficha específica deu match nos filtros locais mas o devedor foi retornado pelo backend,
                            // exibe o devedor na tabela com a primeira ficha para permitir abertura do idDevedorCarteira correto
                            const defaultFicha = dev.fichas && dev.fichas.length > 0 ? dev.fichas[0] : null;
                            const defaultFichaId = defaultFicha ? (defaultFicha.id || defaultFicha.rawId) : null;
                            flattenedData.push({
                                ...dev,
                                nomeCarteira: defaultFicha?.nomeCarteira || dev.nomeCarteira || "-",
                                cvIdFicha: defaultFichaId
                            });
                        }
                    } else if (!updatedTableState.comSaldo) {
                        const defaultFicha = dev.fichas && dev.fichas.length > 0 ? dev.fichas[0] : null;
                        const defaultFichaId = defaultFicha ? (defaultFicha.id || defaultFicha.rawId) : null;
                        flattenedData.push({
                            ...dev,
                            nomeCarteira: defaultFicha?.nomeCarteira || dev.nomeCarteira || "-",
                            cvIdFicha: defaultFichaId
                        });
                    }
                });
            }
            setData(flattenedData);
            setPages(pages || 0);
            setRecordsTotal(flattenedData.length > 0 ? Math.max(recordsTotal || 0, flattenedData.length) : (recordsTotal || 0));
            setLoadingTable(false);
        }).catch(() => {
            setLoadingTable(false);
        });

    }, []);

    useEffect(() => {
        fetchData(tableState.current);
    }, [fetchData]);

    const handleCreate = () => {
        let faseId = filters.fase?.id;

        // Se o filtro não tiver uma fase específica selecionada (Ambas / null / undefined), define com base na equipe do usuário
        if (faseId === null || faseId === undefined) {
            const isJuridicoProfile = userPerfils?.some(p => {
                const n = (p.ccNome || p.nome || (typeof p === 'string' ? p : '')).toLowerCase();
                return n.includes("jur") && !n.includes("extra");
            });
            faseId = userEquipeIsJuridico || Boolean(isJuridicoProfile);
        }

        setIsJuridicoForm(Boolean(faseId));
        setSelectedId(null);
        setIsViewOnly(false);
        setIsCreateEdit(true);
    };

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedId(null);
        setSelectedFichaId(null);
        if (openedFromOrigin === 'monitoria') {
            setOpenedFromOrigin(null);
            const flatRoutes = routes.flatMap(r => r.collapse ? r.collapse : r);
            const monitoriaRoute = flatRoutes.find(r => r.key === "monitoria");
            if (monitoriaRoute) {
                const nav = onPageSelect || window.onPageSelectCustom;
                if (nav) {
                    nav(monitoriaRoute);
                    return;
                }
            }
        } else if (openedFromOrigin === 'monitoria_standalone') {
            setOpenedFromOrigin(null);
            try {
                window.close();
            } catch (e) {}
            return;
        }
        fetchData(tableState.current);
    };

    const handleDeleteClick = (row) => {
        setDevedorToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!devedorToDelete) return;
        postNoResponse("/devedor/delete", { 
            id: devedorToDelete.id, 
            idDevedorCarteira: devedorToDelete.cvIdFicha ? String(devedorToDelete.cvIdFicha) : null 
        })
            .then(() => {
                setSbMessage("Exclusão realizada com sucesso!");
                setSuccessSB(true);
                fetchData(tableState.current);
            })
            .catch(() => {
                setSbMessage("Erro ao excluir. Verifique se existem vínculos.");
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setDevedorToDelete(null);
            });
    };

    // Pergunta "deseja devolver?" e depois a tela de texto/anexo que vão junto da ocorrência (DevolucaoDiretaDialog)
    const handleDevolverClick = (row) => {
        const targetId = row.cvIdFicha ? String(row.cvIdFicha) : row.id;
        setDevolucaoDireta({
            titulo: "Confirmar Devolução",
            pergunta: `Tem certeza que deseja devolver a ficha de ${row.nome || "este devedor"}?`,
            detalhe: "Apenas a ficha desta carteira será devolvida. Demais carteiras do devedor continuarão ativas.",
            endpoint: `/devedor/devolver/${targetId}`,
            idDevedor: row.id,
        });
    };

    const aoConcluirDevolucaoDireta = (avisoAnexo) => {
        setSbMessage(`Ficha devolvida com sucesso!${avisoAnexo}`);
        if (avisoAnexo) setErrorSB(true); else setSuccessSB(true);
        fetchData(tableState.current);
    };

    const handleReativarClick = (row) => {
        setDevedorToReativar(row);
        setReativarDialogOpen(true);
    };

    const confirmReativar = () => {
        if (!devedorToReativar) return;
        const targetId = devedorToReativar.cvIdFicha ? String(devedorToReativar.cvIdFicha) : devedorToReativar.id;
        postNoResponse(`/devedor/reativar/${targetId}`, {})
            .then(() => {
                setSbMessage("Ficha reativada com sucesso!");
                setSuccessSB(true);
                fetchData(tableState.current);
            })
            .catch(() => {
                setSbMessage("Erro ao reativar ficha.");
                setErrorSB(true);
            })
            .finally(() => {
                setReativarDialogOpen(false);
                setDevedorToReativar(null);
            });
    };

    const userColumns = [
        {
            header: "Cód. Cliente",
            accessorKey: "codigoCliente",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Cliente",
            accessorKey: "nomeCliente",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
            isSorted: false
        },
        {
            header: "Carteira",
            accessorKey: "nomeCarteira",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Nome",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Responsável",
            accessorKey: "nomeUsuario",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || info.row.original.responsavel || '-'}</MDTypography> : (info.getValue() || info.row.original.responsavel || '-')}</DefaultCell>,
            isSorted: true
        },
        {
            header: "CPF/CNPJ",
            accessorKey: "cpfCnpj",
            cell: info => <DefaultCell>{info.row.original.status === 1 ? <MDTypography variant="caption" color="error" fontWeight="medium">{info.getValue() || '-'}</MDTypography> : (info.getValue() || '-')}</DefaultCell>,
            isSorted: true
        },
        {
            header: "Status",
            accessorKey: "statusFichaNome",
            cell: info => {
                const isDevolvido = info.row.original.status === 1 || info.row.original.situacao === 1;
                const isQuitado = info.row.original.status === 7 || info.row.original.situacao === 7;
                const displayValue = info.getValue() || info.row.original.status || "-";
                
                if (isDevolvido) {
                    const tooltipText = info.row.original.dataDevolucao
                        ? `Devolvida em ${toDateTime(info.row.original.dataDevolucao)}${info.row.original.nomeUsuarioDevolucao ? ` por ${info.row.original.nomeUsuarioDevolucao}` : ""}`
                        : "Ficha Devolvida";

                    return (
                        <DefaultCell>
                            <Tooltip title={tooltipText} arrow>
                                <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ cursor: "pointer" }}>
                                    {displayValue} (Devolvido)
                                </MDTypography>
                            </Tooltip>
                        </DefaultCell>
                    );
                }

                if (isQuitado) {
                    return (
                        <DefaultCell>
                            <MDTypography variant="caption" sx={{ color: darkMode ? "#ce93d8" : "#8E24AA", fontWeight: "bold" }}>
                                {String(displayValue).toLowerCase().includes("quitad") ? displayValue : `${displayValue} (Quitado)`}
                            </MDTypography>
                        </DefaultCell>
                    );
                }
                
                return <DefaultCell>{displayValue}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <MDBox display="flex">
                    {userFunctionalities?.includes("c050_V") && !userFunctionalities?.includes("c050_U") && (
                        <Tooltip title="Visualizar Cadastro">
                            <IconButton
                                onClick={() => {
                                    setSelectedId(info.row.original.id);
                                    setSelectedFichaId(info.row.original.cvIdFicha ? String(info.row.original.cvIdFicha) : null);
                                    setIsViewOnly(true);
                                    setIsCreateEdit(true);
                                }}>
                                <MDTypography variant="h5">
                                    <Icon color="info">visibility</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    )}
                    {userFunctionalities?.includes("c050_U") && (
                        <Tooltip title="Editar Cadastro">
                            <IconButton
                                onClick={() => {
                                    setSelectedId(info.row.original.id);
                                    setSelectedFichaId(info.row.original.cvIdFicha ? String(info.row.original.cvIdFicha) : null);
                                    setIsViewOnly(false);
                                    setIsCreateEdit(true);
                                }}>
                                <MDTypography variant="h5">
                                    <Icon color="warning">edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    )}
                    {userFunctionalities?.includes("c050_D") && (
                        <Tooltip title="Excluir Ficha">
                            <IconButton onClick={() => handleDeleteClick(info.row.original)}>
                                <MDTypography variant="h5">
                                    <Icon color="error">delete</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    )}
                    {podeDevolverDireto && (info.row.original.status !== 1 && info.row.original.situacao !== 1) && (
                        <Tooltip title="Devolver Ficha">
                            <IconButton onClick={() => handleDevolverClick(info.row.original)}>
                                <MDTypography variant="h5">
                                    <Icon color="warning">undo</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    )}
                    {podeDevolverDireto && (info.row.original.status === 1 || info.row.original.situacao === 1) && (
                        <Tooltip title="Reativar Ficha">
                            <IconButton onClick={() => handleReativarClick(info.row.original)}>
                                <MDTypography variant="h5">
                                    <Icon color="success">restore</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    )}
                </MDBox>
            ),
            width: "10%"
        },
    ];

    const handleApplyFilter = () => {
        fetchData({
            ...tableState.current,
            cvId: filters.cvId,
            nome: filters.nome,
            cpfCnpj: cleanFormatCNPJ(filters.cpfCnpj),
            codigoCliente: filters.codigoCliente,
            titulo: filters.titulo,
            processo: filters.processo,
            status: filters.status,
            idUsuario: filters.idUsuario?.id,
            fase: filters.fase,
            hideDevolvidos: filters.hideDevolvidos,
            comSaldo: filters.comSaldo,
            cliente: filters.cliente,
            pageIndex: 0
        });
    };

    const handleCleanFilter = () => {
        setFilters({
            cvId: "",
            nome: "",
            cpfCnpj: "",
            codigoCliente: "",
            titulo: "",
            processo: "",
            status: null,
            idUsuario: usuariosOptions[0] || { id: null, nome: "Todos" },
            fase: faseOptions[0],
            hideDevolvidos: false,
            comSaldo: false,
            cliente: null,
        });
        const updatedTableState = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: [],
            cvId: "",
            nome: "",
            cpfCnpj: "",
            codigoCliente: "",
            titulo: "",
            processo: "",
            status: null,
            idUsuario: null,
            fase: null,
            hideDevolvidos: false,
            comSaldo: false,
            cliente: null,
        };
        tableState.current = updatedTableState;
        fetchData(updatedTableState);
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <BaseDeDadosCreateEdit recordId={selectedId} idDevedorCarteira={selectedFichaId} closeCreateEdit={backFunction} isJuridico={isJuridicoForm} isViewOnly={isViewOnly} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? "rgba(255,255,255,0.15)" : "#e0e0e0",
                        borderRadius: "8px",
                    }}
                >
                    <Grid container spacing={1.2}>
                        {/* Linha 1: 5 campos rigorosamente proporcionais [1.5, 3, 2, 2, 3.5] */}
                        <Grid item xs={12} sm={1.5}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>ID</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="ID..."
                                value={filters.cvId ?? ""}
                                onChange={(e) => setFilters({ ...filters, cvId: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Nome</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Nome do devedor..."
                                value={filters.nome ?? ""}
                                onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>CPF / CNPJ</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="CPF ou CNPJ..."
                                value={filters.cpfCnpj ?? ""}
                                onChange={(e) => setFilters({ ...filters, cpfCnpj: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Cód. Cliente</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Cód. do cliente..."
                                value={filters.codigoCliente ?? ""}
                                onChange={(e) => setFilters({ ...filters, codigoCliente: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3.5}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Cliente</InputLabel>
                            <Autocomplete
                                options={pessoaOptions}
                                filterOptions={(x) => x}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.nomeComum || option.nomeCompleto || "";
                                }}
                                onInputChange={(e, val) => handleSearchPessoas(val)}
                                onChange={(e, val) => setFilters({ ...filters, cliente: val })}
                                value={filters.cliente || null}
                                loading={loadingPessoas}
                                sx={{
                                    "& .MuiAutocomplete-endAdornment": {
                                        top: "50% !important",
                                        transform: "translateY(-50%) !important",
                                        right: "6px !important",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "2px"
                                    },
                                    "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
                                        padding: "2px !important",
                                        color: darkMode ? "rgba(255, 255, 255, 0.6) !important" : "#7b809a !important",
                                        "&:hover": {
                                            color: darkMode ? "#ffffff !important" : "#344767 !important",
                                            backgroundColor: "transparent !important",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "1.1rem !important",
                                        }
                                    }
                                }}
                                renderInput={(params) => (
                                    <MDInput
                                        {...params}
                                        size="small"
                                        placeholder="Buscar cliente..."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingPessoas ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Linha 2: 5 campos rigorosamente proporcionais [1.5, 3, 2, 2, 3.5] */}
                        <Grid item xs={12} sm={1.5}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Título</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Nº título..."
                                value={filters.titulo ?? ""}
                                onChange={(e) => setFilters({ ...filters, titulo: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Processo</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Nº processo..."
                                value={filters.processo ?? ""}
                                onChange={(e) => setFilters({ ...filters, processo: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Status</InputLabel>
                            <MDSelect
                                size="small"
                                isMulti={true}
                                isClearable={true}
                                getOptionValue={(option) => option.id ?? ""}
                                getOptionLabel={(option) => option.ccNome}
                                name="status"
                                placeholder="Todos"
                                value={filters.status || null}
                                options={[{ id: null, ccNome: "Todos" }, ...statusFichaOptions]}
                                onChange={(value) => {
                                    if (!value || value.length === 0 || (Array.isArray(value) && value.some(v => v.id === null))) {
                                        setFilters({ ...filters, status: null });
                                    } else {
                                        setFilters({ ...filters, status: value });
                                    }
                                }}
                            />
                        </Grid>
                        {!hideFaseFilter ? (
                            <Grid item xs={12} sm={2}>
                                <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Fase</InputLabel>
                                <MDSelect
                                    size="small"
                                    isMulti={false}
                                    isClearable={true}
                                    getOptionValue={(option) => option.id ?? ""}
                                    getOptionLabel={(option) => option.nome}
                                    name="fase"
                                    placeholder="Todos"
                                    value={filters.fase && (typeof filters.fase === 'object' ? filters.fase.id !== null : filters.fase !== null)
                                        ? (faseOptions.find(o => o.id === (filters.fase?.id !== undefined ? filters.fase.id : filters.fase)) || null)
                                        : null}
                                    options={faseOptions}
                                    onChange={(value) => {
                                        setFilters({ ...filters, fase: value || faseOptions[0] })
                                    }}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={2} />
                        )}
                        {isLiberado ? (
                            <Grid item xs={12} sm={3.5}>
                                <InputLabel sx={{ px: 0.5, pb: 0.3 }}>Responsável</InputLabel>
                                <MDSelect
                                    size="small"
                                    isMulti={false}
                                    isClearable={true}
                                    getOptionValue={(option) => option.id ?? ""}
                                    getOptionLabel={(option) => option.nome}
                                    name="idUsuario"
                                    placeholder="Todos"
                                    value={filters.idUsuario && (typeof filters.idUsuario === 'object' ? filters.idUsuario.id !== null : filters.idUsuario !== null)
                                        ? (usuariosOptions.find(u => u.id === (filters.idUsuario?.id !== undefined ? filters.idUsuario.id : filters.idUsuario)) || null)
                                        : null}
                                    options={usuariosOptions}
                                    onChange={(value) => {
                                        setFilters({ ...filters, idUsuario: value || usuariosOptions[0] })
                                    }}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={3.5} />
                        )}

                        {/* Linha 3: Switches no padrão do sistema na esquerda e Botões no padrão do sistema na direita */}
                        <Grid item xs={12} display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" mt={1} pt={0.5}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={Boolean(filters.hideDevolvidos)}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setFilters((prev) => ({ ...prev, hideDevolvidos: isChecked }));
                                                fetchData({
                                                    ...tableState.current,
                                                    hideDevolvidos: isChecked,
                                                    pageIndex: 0
                                                });
                                            }}
                                            color="info"
                                        />
                                    } 
                                    label={<MDTypography variant="button" fontWeight="regular" color="text" sx={{ cursor: "pointer", userSelect: "none", ml: -0.5, whiteSpace: "nowrap" }}>Exceto Devolvidos</MDTypography>}
                                    sx={{ margin: 0 }}
                                />
                                <FormControlLabel 
                                    control={
                                        <Switch 
                                            checked={Boolean(filters.comSaldo)}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                setFilters((prev) => ({ ...prev, comSaldo: isChecked }));
                                                fetchData({
                                                    ...tableState.current,
                                                    comSaldo: isChecked,
                                                    pageIndex: 0
                                                });
                                            }}
                                            color="info"
                                        />
                                    } 
                                    label={<MDTypography variant="button" fontWeight="regular" color="text" sx={{ cursor: "pointer", userSelect: "none", ml: -0.5, whiteSpace: "nowrap" }}>Com Saldo</MDTypography>}
                                    sx={{ margin: 0 }}
                                />
                            </MDBox>
                            
                            <MDBox display="flex" alignItems="center">
                                <MDButton
                                    size="medium"
                                    sx={{ marginRight: 2 }}
                                    variant="contained"
                                    color="info"
                                    onClick={handleCleanFilter}
                                >
                                    Limpar filtro
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    sx={{ marginRight: userFunctionalities?.includes("c050_C") ? 2 : 0 }}
                                    variant="contained" 
                                    color="info" 
                                    onClick={handleApplyFilter}
                                >
                                    Aplicar filtro
                                </MDButton>
                                {userFunctionalities?.includes("c050_C") && (
                                    <MDButton 
                                        size="medium"
                                        variant="gradient" 
                                        color="success" 
                                        onClick={handleCreate}
                                    >
                                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                        &nbsp;NOVO CADASTRO
                                    </MDButton>
                                )}
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    tableState={tableState} 
                />
            </Grid>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja excluir a ficha de <strong>{devedorToDelete?.nome}</strong>?
                    <br/><br/>
                    <MDTypography variant="caption" color="text">
                        Caso o devedor possua múltiplas carteiras, apenas a ficha desta carteira será excluída. Se possuir apenas esta ficha cadastrada, o devedor será excluído por completo.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} color="secondary">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            <DevolucaoDiretaDialog alvo={devolucaoDireta} onClose={() => setDevolucaoDireta(null)} onConcluida={aoConcluirDevolucaoDireta} />

            <Dialog open={reativarDialogOpen} onClose={() => setReativarDialogOpen(false)}>
                <DialogTitle>Confirmar Reativação</DialogTitle>
                <DialogContent>
                    Tem certeza que deseja reativar a ficha de <strong>{devedorToReativar?.nome}</strong>?
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setReativarDialogOpen(false)} color="secondary">
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmReativar} color="success" variant="contained">
                        Reativar
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => {
                    setSuccessSB(false);
                    setErrorSB(false);
                }}
                close={() => {
                    setSuccessSB(false);
                    setErrorSB(false);
                }}
                bgWhite
            />
        </Grid>
    );
}
